<i18n>
{
  "fr": {
    "no-result": "Aucun résultat, veuillez vérifier l'adresse"
  }
}
</i18n>

<template>
  <div
    class="address-input"
    :class="{
      'label-input--icon-before-input': iconBeforeInput,
      'address-input--dropdown-opened': isDropdownOpened,
    }"
  >
    <input
      :id="id"
      v-model="model"
      :name="name"
      type="text"
      class="label-input__input address-input__input"
      :required="required"
      :readonly="readonly"
      :disabled="disabled"
      autocomplete="none"
      :autofocus="autofocus"
      :placeholder="placeholder"
      @click="onInputClick"
    />

    <i
      v-show="isAutocompleteAvailable"
      class="label-input__icon address-input__icon"
    ></i>

    <div v-show="isDropdownOpened">
      <ul v-if="addresses.length" class="address-input__dropdown">
        <li
          v-for="address in addresses"
          :key="address.id"
          class="address-input__suggestion"
          @click="addressClicked(address)"
        >
          {{ formattedAddress(address) }}
        </li>
      </ul>

      <div
        v-else
        class="address-input__dropdown address-input__dropdown--no-result"
      >
        {{ $t('no-result') }}
      </div>
    </div>
  </div>
</template>

<script>
const API_URL = 'https://photon.komoot.io/api/';
const SUPPORTED_COUNTRIES = ['FR', 'BE'];
const COUNTRY_LABELS = {
  FR: 'France',
  BE: 'Belgique',
};

export default {
  name: 'AddressInput',
  props: {
    id: {
      type: String,
      required: true,
    },
    name: {
      type: String,
      required: true,
    },
    value: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    addressComplementInputSelector: {
      type: String,
      default: 'input[name="address2"]',
    },
    postcodeInputSelector: {
      type: String,
      default: 'input[name="postcode"]',
    },
    cityInputSelector: {
      type: String,
      default: 'input[name="city"]',
    },
    countryInputSelector: {
      type: String,
      default: 'select[name="country"]',
    },
    required: Boolean,
    readonly: Boolean,
    disabled: Boolean,
    autofocus: Boolean,
    iconBeforeInput: Boolean,
    onAddressClick: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      model: this.value,
      addresses: [],
      countryInput: null,
      selectedCountry: null,
      isDropdownOpened: false,
      timeout: null,
      preventApiCallAfterAutocomplete: false,
    };
  },
  computed: {
    activeCountries() {
      return this.countryInput ? [this.selectedCountry] : SUPPORTED_COUNTRIES;
    },
    isAutocompleteAvailable() {
      return this.activeCountries.some(country =>
        SUPPORTED_COUNTRIES.includes(country)
      );
    },
  },
  watch: {
    /**
     * @param {string} value
     */
    model(value) {
      if (null !== this.timeout) {
        clearTimeout(this.timeout);
      }

      if (this.preventApiCallAfterAutocomplete) {
        this.preventApiCallAfterAutocomplete = false;
      } else if ((value || '').length > 0 && this.isAutocompleteAvailable) {
        this.timeout = setTimeout(() => this.callApi(value), 350);
      }
    },
  },
  mounted() {
    this.countryInput = document.querySelector(this.countryInputSelector);

    if (null !== this.countryInput) {
      this.addCountryInputChangeListener();
    }

    this.addDocumentClickListener();
  },
  beforeDestroy() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }

    if (this.countryInput) {
      this.countryInput.removeEventListener(
        'change',
        this.onCountryInputChange
      );
    }

    document.removeEventListener('click', this.onDocumentClick);
  },
  methods: {
    addCountryInputChangeListener() {
      this.countryInput.addEventListener('change', this.onCountryInputChange);

      this.selectedCountry = this.countryInput.value;
    },
    addDocumentClickListener() {
      document.addEventListener('click', this.onDocumentClick);
    },
    onCountryInputChange(event) {
      this.selectedCountry = event.target.value;
    },
    onDocumentClick(event) {
      if (!this.$el.contains(event.target)) {
        this.isDropdownOpened = false;
      }
    },
    /**
     * @param {string} address
     * @returns {Promise<any>}
     */
    callApi(address) {
      return fetch(
        `${API_URL}?${new URLSearchParams({
          q: address,
          lang: 'fr',
        })}`,
        {
          method: 'get',
        }
      )
        .then(res => res.json())
        .then(({ features = [] }) => {
          this.addresses = features
            .map(({ properties }) => this.normalizeAddress(properties))
            .filter(
              normalizedAddress =>
                normalizedAddress.postcode &&
                normalizedAddress.city &&
                this.activeCountries.includes(normalizedAddress.countryCode)
            );

          this.isDropdownOpened = true;
        })
        .catch(() => {
          this.addresses = [];
          this.isDropdownOpened = true;
        });
    },
    /**
     * @param {Object} properties
     * @returns {Object}
     */
    normalizeAddress(properties) {
      const street = properties.street || properties.name;
      const houseNumber = properties.housenumber;

      return {
        id: properties.osm_id,
        name: [houseNumber, street].filter(Boolean).join(' '),
        postcode: properties.postcode,
        city: properties.city || properties.locality || properties.county,
        countryCode: (properties.countrycode || '').toUpperCase(),
      };
    },
    /**
     * @param {Object} address
     * @returns {string}
     */

    formattedAddress(address) {
      return `${address.name}, ${this.normalizePostcode(address)} ${
        address.city
      } ${getCountry(address.countryCode)}`;
    },
    /**
     * @param {string} name
     * @param {string} postcode
     * @param {string} city
     * @param {string} countryCode
     */
    addressClicked({ name, postcode, city, countryCode }) {
      this.preventApiCallAfterAutocomplete = true;
      this.model = name;
      this.isDropdownOpened = false;

      const normalizedPostcode = this.normalizePostcode({
        postcode,
        countryCode,
      });

      if (this.onAddressClick) {
        this.onAddressClick({
          name,
          postcode: normalizedPostcode,
          city,
          countryCode,
        });
      } else {
        document.querySelector(this.addressComplementInputSelector).value =
          null;
        document.querySelector(this.postcodeInputSelector).value =
          normalizedPostcode;
        document.querySelector(this.cityInputSelector).value = city;
        document.querySelector(this.countryInputSelector).value = countryCode;
      }
    },
    onInputClick() {
      this.isDropdownOpened = this.addresses.length > 0;
    },
    normalizePostcode({ postcode, countryCode }) {
      return countryCode === 'FR' ? postcode.split(';')[0].trim() : postcode;
    },
  },
};

function getCountry(countryCode) {
  return COUNTRY_LABELS[countryCode] || countryCode;
}
</script>
